import './EditDocument.scss';

import { ChangeEvent, FC, FocusEvent, useContext, useEffect, useRef, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import BjxArticleEditor, { ReRenderAction } from 'components/BjxArticleEditor/BjxArticleEditor';
import { useAIGenerator } from 'customHooks/useAIGenerator';
import { defaultTopNumberResults, useDataSet } from 'customHooks/useDataSet';
import { useDocumentLinks } from 'customHooks/useDocumentLinks';
import { useGuidedNavigationCollection } from 'customHooks/useGuidedNavigationCollection';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useSingleEntity } from 'customHooks/useSingleEntity';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { EditorInterface, TourContext } from 'data/intros/TourContext';
import { isEmptyObject } from 'functions/isEmptyObject';
import DataWrapper from 'interfaces/DataWrapper';
import { ArticleType, Document } from 'models/Document';
import { DocumentComment } from 'models/DocumentComment';
import { DocumentStatus } from 'models/DocumentStatus';
import { DocumentStatusKey } from 'models/DocumentStatusKey';
import { Entities } from 'models/Entities';
import { ErrorCause } from 'models/ErrorResponse';
import { FileInfo } from 'models/FileInfo';
import { LogLevel } from 'models/LogLevel';
import { PromptFlow, PromptFlowExecution, TargetAction } from 'models/PromptFlow';
import { SiteSection } from 'models/SiteSection';
import { TargetKeyword } from 'models/TargetKeyword';
import { Theme } from 'models/Theme';
import WorkflowHelp from 'modules/Documents/WorkflowHelp/WorkflowHelp';
import moment from 'moment';
import { Card, Col, Form, InputGroup, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import InputGroupText from 'react-bootstrap/esm/InputGroupText';
import ReactGA from "react-ga4";
import { useNavigate, useParams } from 'react-router-dom';
import { SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';

import CoverImageContainer from '../CoverImageContainer/CoverImageContainer';
import EditorSidebar from '../EditorSidebar/EditorSidebar';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditDocumentProps { }

const EditDocument: FC<EditDocumentProps> = () => {
  const newEntryId = 'new';
  const navigate = useNavigate();
  const { id } = useParams();
  const documentId: number | undefined = id !== newEntryId ? parseInt(id!, 10) : undefined;
  const {
    configRepo,
    setIsProjectSelectable,
    selectedProjectId,
    setSelectedProjectIdAsync,
    notify,
    sendLog
  } = useContext(AppContext);
  const defaultDate = new Date();
  defaultDate.setDate(defaultDate.getDate() + 7);
  const defaultDocument: Document = {
    id: 0,
    statusId: 0,
    content: '',
    projectId: selectedProjectId!,
    imageUrl: '',
    keywordId: undefined,
    siteSectionId: undefined,
    articleType: undefined,
    shortDescription: '',
    fbMessageTmp: '',
    twMessageTmp: '',
    liMessageTmp: '',
    title: '',
    plannedDate: defaultDate,
    actualDate: defaultDate,
    targetLength: 500,
    cmsPageId: undefined,
  };
  const defaultKeyword: TargetKeyword = {
    id: 0,
    seoKeyword: '',
    seoMonthlySearchVolume: 0,
    seoDifficulty: 0,
  };
  const [cachedDocument, setCachedDocument] = useState<Document>(defaultDocument);
  const [cachedKeyword, setCachedKeyword] = useState<TargetKeyword>(defaultKeyword);
  const [bannerImage, setBannerImage] = useState<FileInfo | undefined>(undefined);
  const docRef = useRef<Document>();
  docRef.current = cachedDocument;

  const predefinedLinks = useDocumentLinks(configRepo!.SCHEDULE_API_BASE_URI);

  const onTokenExpired = useTokenExpiredHandler();
  const { createEntity: createDocument, updateEntity: updateDocument } =
    useModifyEntityService<Document>(
      configRepo!.SCHEDULE_API_BASE_URI,
      Entities.Documents,
      sendLog,
      onTokenExpired,
    );
  const { createEntity: createKeyword, updateEntity: updateKeyword } =
    useModifyEntityService<TargetKeyword>(
      configRepo!.SCHEDULE_API_BASE_URI,
      Entities.TargetKeywords,
      sendLog,
      onTokenExpired,
    );

  const contentGenerator = useAIGenerator(configRepo!.SCHEDULE_API_BASE_URI);

  const tour = useGuidedNavigationCollection();

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  const { entityRequest: documentRequest } = useSingleEntity<Document>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    documentId,
  );
  const loadedDocument: Document = documentRequest.entity;
  const { dataSet: statusesDataSet } = useDataSet<DocumentStatus>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.DocumentStatuses,
    selectedProjectId,
  );

  const statuses: DocumentStatus[] = statusesDataSet.responseData as DocumentStatus[];
  useEffect(() => {
    const updatedCachedDocument: Document = { ...cachedDocument! };
    if (updatedCachedDocument.statusId === 0 && statuses.length > 0) {
      updatedCachedDocument.statusId = statuses[0].id;
      setCachedDocument(updatedCachedDocument);
    }
  }, [statuses]);

  const { dataSet: sectionsDataSet, setOperators: setSectionsOperators } = useDataSet<SiteSection>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.SiteSections,
    selectedProjectId,
  );
  const sections: SiteSection[] = sectionsDataSet.responseData as SiteSection[];

  const { dataSet: themesDataSet, setOperators: setThemesOperators } = useDataSet<Theme>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Themes,
    selectedProjectId,
  );
  const themes: Theme[] = (themesDataSet.responseData as DataWrapper<Theme>).data;
  const [themeOptions, setThemeOptions] = useState<Array<{ value: string, label: string }>>([]);
  const defaultThemeOption: { value: string, label: string } = { value: '', label: '' };
  const [selectedTheme, setSelectedTheme] = useState<{ value: string, label: string }>(defaultThemeOption);
  useEffect(() => {
    if (themes && themes.length > 0) {
      const themeOpts = themes.map(theme => ({ value: theme.id!.toString(), label: theme.name }));
      setThemeOptions(themeOpts);

      if (cachedDocument) {
        let themeOption: { value: string, label: string };
        if (cachedDocument.themeId) {
          themeOption = themeOpts.find(t => t.value === cachedDocument.themeId!.toString())!;
        } else {
          themeOption = defaultThemeOption;
        }
        setSelectedTheme(themeOption);
      }
    } else {
      setSelectedTheme(defaultThemeOption);
    }
  }, [themes, cachedDocument]);

  const searchThemes = async (inputValue: string) => {
    const results = themeOptions.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
    return results;
  };

  const handleThemeChanged = (selection: SingleValue<{ value: string, label: string }>) => {
    const updatedDocument = { ...cachedDocument };
    if (selection) {
      const updatedTheme: Theme | undefined = themes.find(t => t.id!.toString() === selection!.value);
      updatedDocument.themeId = updatedTheme ? updatedTheme.id : undefined;
    } else {
      updatedDocument.themeId = undefined;
    }
    setCachedDocument(updatedDocument);
    setSelectedTheme(selection || defaultThemeOption);
  };

  useEffect(() => {
    const updatedCachedDocument: Document = { ...cachedDocument! };
    if (!updatedCachedDocument.siteSectionId && sections.length > 0) {
      updatedCachedDocument.siteSectionId = sections[0].id;
      setCachedDocument(updatedCachedDocument);
    }
  }, [sections]);

  const { entityRequest: keywordRequest, setEntityRequest: setKeywordRequest } =
    useSingleEntity<TargetKeyword>(
      configRepo!.SCHEDULE_API_BASE_URI,
      Entities.TargetKeywords,
      loadedDocument.keywordId,
    );
  const loadedKeyword: TargetKeyword = keywordRequest.entity;

  const { dataSet: commentsDataset } = useDataSet<DocumentComment>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.DocumentComments,
    undefined,
    { DocumentId: documentId },
  );
  const comments: DocumentComment[] = commentsDataset.responseData as DocumentComment[];

  useEffect(() => {
    const updatedCachedDocument: Document = { ...cachedDocument! };
    updatedCachedDocument.imageUrl = bannerImage?.url ?? undefined;
    setCachedDocument(updatedCachedDocument);
  }, [bannerImage]);

  const configureLoadedDocumentDependencies = async (doc: Document) => {
    if (doc.keywordId) {
      setKeywordRequest({ type: 'entityId', value: doc.keywordId });
    }

    if (doc.projectId && doc.projectId !== selectedProjectId) {
      await setSelectedProjectIdAsync(doc.projectId);
      const defaultOperators = { Top: defaultTopNumberResults, ProjectId: doc.projectId };
      setSectionsOperators(defaultOperators);
      setThemesOperators(defaultOperators);
    }

    if (doc.imageUrl) {
      setBannerImage({
        name: doc.imageUrl.split('/').reverse()[0],
        url: doc.imageUrl,
      } as FileInfo);
    }

  }

  useEffect(() => {
    // only trigger intro render if the user is creating a new document, otherwise render is called later, after document is loaded
    if (id === newEntryId && !loadedDocument.id && statuses.length > 0 && selectedProjectId) {
      tour.startTourFromQueryParams();
    }
  }, [id, loadedDocument.id, statuses, selectedProjectId]);

  useEffect(() => {
    if (documentRequest.isLoading) {
      return;
    }

    if (documentRequest.isError && documentRequest.error?.cause === ErrorCause.NotFoundError) {
      notify('Document not found.', NotificationType.Error);
      navigate('/documents', { replace: true });
      return;
    }

    const updatedCachedDocument = { ...loadedDocument };

    if (isEmptyObject(updatedCachedDocument)) {
      return;
    }

    setCachedDocument(updatedCachedDocument);

    const configureDocDeps = async () => {
      await configureLoadedDocumentDependencies(updatedCachedDocument);
      tour.startTourFromQueryParams();
    };
    configureDocDeps().catch((error) => {
      sendLog(`Error configuring document dependencies: ${error}`, LogLevel.error, { error, documentId: updatedCachedDocument.id });
    });

  }, [loadedDocument, documentRequest.isLoading]);

  useEffect(() => {
    setCachedKeyword(loadedKeyword);
  }, [loadedKeyword]);

  const onDocumentFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedCachedDocument = { ...cachedDocument };

    if (event.target.type === 'date') {
      const selectedDate = event.target.valueAsDate!;
      if (!selectedDate) return;
      (updatedCachedDocument as any)[event.target.name] = new Date(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
      );
    } else {
      (updatedCachedDocument as any)[event.target.name] = event.target.value;
    }

    setCachedDocument(updatedCachedDocument);
  };

  const onKeywordFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedCachedKeyword = { ...cachedKeyword };
    (updatedCachedKeyword as any)[event.target.name] = event.target.value;
    setCachedKeyword(updatedCachedKeyword);
  };

  const onSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const updatedCachedDocument = { ...cachedDocument };
    (updatedCachedDocument as any)[event.target.name] = event.target.value;
    setCachedDocument(updatedCachedDocument);
  };

  const onEditorChange = (value: string) => {
    const updatedCachedDocument = { ...docRef.current! };
    updatedCachedDocument.content = value;
    setCachedDocument(updatedCachedDocument);
  };

  const saveDocument = async (document: Document, publishNow: boolean): Promise<boolean> => {
    const queryParams = new URLSearchParams();
    if (publishNow) {
      queryParams.append('publishNow', 'true');
    }

    if (id === newEntryId) {
      const result = await createDocument(document, queryParams);
      if (result) {
        setCachedDocument(result);
        navigate(window.location.pathname.replace(newEntryId, result.id.toString()), {
          replace: true,
        });

        const tourContext = (window as any as TourContext<EditorInterface>);
        if (!tourContext.args) {
          tourContext.args = {};
        }
        tourContext.args.isDocumentSubmitted = true;
        tourContext.args.documentId = result.id;

        return true;
      }
      return false;
    }
    return updateDocument(document, queryParams);
  };

  const saveKeywordIfNotNull = async (
    keywordId: number | undefined,
    documentToUpdate: Document,
  ): Promise<Document> => {
    const updatedDoc = { ...documentToUpdate };

    if (cachedKeyword.seoKeyword) {
      let updateSucceeded = false;

      if (keywordId) {
        updateSucceeded = await updateKeyword(cachedKeyword);
      } else {
        const result = await createKeyword(cachedKeyword);
        if (result) {
          setCachedKeyword(result);
          updatedDoc.keywordId = result.id;
          setCachedDocument(updatedDoc);
          updateSucceeded = true;
        }
      }

      const message = `Keyword "${cachedKeyword.seoKeyword}" was ${updateSucceeded ? '' : 'NOT'} updated successfully`;
      sendLog(message, LogLevel.info);

      if (!updateSucceeded) {
        notify('Keyword for document failed to save or update.', NotificationType.Error);
        throw new Error('Keyword failed to save');
      }
    }

    return updatedDoc;
  };

  const logAndNotifyDocumentSaveFailure = (documentForUpdate: Document) => {
    notify('Document has NOT been saved successfully.', NotificationType.Error);
    sendLog(`Document has NOT been updated successfully, ${documentForUpdate.id}`, LogLevel.error, { documentId: documentForUpdate.id });
  };

  const handleSave = async (publishNow: boolean, overrideStatusKey: DocumentStatusKey | undefined = undefined): Promise<boolean> => {
    let documentForUpdate = { ...cachedDocument };
    documentForUpdate = await saveKeywordIfNotNull(cachedDocument.keywordId, documentForUpdate);

    let republishOrPublishNow = publishNow;

    if (overrideStatusKey) {
      const explicitStatusToSet: DocumentStatus | undefined = statuses.find((s) => s.type === overrideStatusKey);
      if (!explicitStatusToSet) {
        sendLog(`Failed to find status for key: ${overrideStatusKey}, cannot explictly override document status.`, LogLevel.error);
      } else {
        documentForUpdate.statusId = explicitStatusToSet.id;
      }
      // if we are attempting to explicit set status then we should unset this request for immed publish
      republishOrPublishNow = false;
    }

    if (statuses.length > 0) {
      const publishedStatus: DocumentStatus = statuses.find((s) => s.type === 'PUBLISHED')!;
      if (cachedDocument.statusId === publishedStatus.id) {
        republishOrPublishNow = true;
      }
    }

    const hasSavedSuccessfully = await saveDocument(documentForUpdate, republishOrPublishNow);
    if (!hasSavedSuccessfully) {
      logAndNotifyDocumentSaveFailure(documentForUpdate);
      return false;
    }

    sendLog(`Document has been scheduled successfully, ${documentForUpdate.id}`, LogLevel.info);
    notify('Document has been scheduled successfully.', NotificationType.Success);
    setCachedDocument(documentForUpdate);

    return true;
  };

  const onSave = async (publishNow: boolean, overrideStatusKey: DocumentStatusKey | undefined = undefined) => {
    let actionLabel;
    if (overrideStatusKey) {
      actionLabel = overrideStatusKey.toString().toLowerCase();
    } else if (publishNow) {
      actionLabel = 'publish_now';
    } else {
      actionLabel = 'schedule';
    }

    ReactGA.event({
      category: "document_editor",
      action: "on_save",
      label: actionLabel, // optional
      value: 1, // optional, must be a number
      nonInteraction: false, // optional, true/false
      transport: "xhr", // optional, beacon/xhr/image
    });
    await handleSave(publishNow, overrideStatusKey);
  };

  const onSaveAndNew = async () => {
    ReactGA.event({
      category: "document_editor",
      action: "on_save",
      label: 'save_and_new', // optional
      value: 1, // optional, must be a number
      nonInteraction: false, // optional, true/false
      transport: "xhr", // optional, beacon/xhr/image
    });
    const isSuccessful = await handleSave(false);
    if (isSuccessful && id) {
      window.location.href = window.location.pathname.replace(id, newEntryId);
    }
  };

  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const onGenerateContent = async (promptFlowId: number): Promise<PromptFlowExecution> => {
    let content: PromptFlowExecution = {
      data: undefined,
      lastResponse: '',
      parsedResult: '',
      errorMessage: '',
      conversation: [],
    };
    if (!isGeneratingContent) {
      try {
        setIsGeneratingContent(true);
        const draftDoc: Document = { ...docRef.current! };
        if (!draftDoc?.title) {
          notify(
            'Document has not yet loaded completely, please try again in a few seconds.',
            NotificationType.Warning,
          );
          return content;
        }
        (draftDoc as any).keyword = { ...cachedKeyword };
        content = await contentGenerator.generateContent(draftDoc, promptFlowId);
      } finally {
        setIsGeneratingContent(false);
      }
    }
    return content;
  };

  const [plannedDate, setPlannedDate] = useState<Date>(cachedDocument.plannedDate!);
  const [plannedTime, setPlannedTime] = useState<string>(moment(cachedDocument.plannedDate).format('HH'));

  const validateOrAdjustEnteredDate = (enteredDate: Date) => {
    const minDate = new Date(moment(new Date()).add(1, 'hours').format('YYYY-MM-DDTHH:00'));
    const maxDate = moment(new Date()).add(1, 'years').toDate();

    if (enteredDate < minDate) {
      notify('Planned date cannot be earlier than one hour from now.', NotificationType.Warning);
      return minDate;
    }

    if (enteredDate > maxDate) {
      notify(
        'Planned date must be within 1 year from today.',
        NotificationType.Warning,
      );
      return maxDate;
    }

    return enteredDate;
  };

  const [selectedDateString, setSelectedDateString] = useState(moment(cachedDocument.plannedDate).format('YYYY-MM-DD'));
  const onPlannedDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDateString(event.target.value);
  };
  /* eslint-disable no-param-reassign */
  const onSelectPlannedDate = (event: FocusEvent<HTMLInputElement>) => {
    const updatedCachedDocument = { ...cachedDocument };
    const selectedDate = moment(event.target.value, 'YYYY-MM-DD');
    if (!selectedDate.isValid()) {
      return;
    }

    const hours = parseInt(plannedTime, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(hours)) return;

    let selectedDateTime = selectedDate.add(hours, 'hours').toDate();
    selectedDateTime = validateOrAdjustEnteredDate(selectedDateTime);

    updatedCachedDocument.plannedDate = selectedDateTime;
    setPlannedDate(selectedDateTime);
    setSelectedDateString(moment(selectedDateTime).format('YYYY-MM-DD'));
    if (selectedDateTime.getHours() !== hours) {
      const time = selectedDateTime.getHours();
      setPlannedTime(time.toString());
    }
    setCachedDocument(updatedCachedDocument);
  };

  /* eslint-disable no-param-reassign */
  const onSelectPlannedTime = (event: ChangeEvent<HTMLSelectElement>) => {
    const updatedCachedDocument = { ...cachedDocument };

    const selectedHour = parseInt(event.target.value, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(selectedHour)) return;

    const date = plannedDate.setHours(0, 0, 0, 0);
    let selectedDateTime = moment(date).add(selectedHour, 'hours').toDate();
    selectedDateTime = validateOrAdjustEnteredDate(selectedDateTime);

    updatedCachedDocument.plannedDate = selectedDateTime;

    const time = selectedDateTime.getHours();
    setPlannedTime(time.toString());
    if (selectedDateTime !== plannedDate) {
      setPlannedDate(selectedDateTime);
      setSelectedDateString(moment(selectedDateTime).format('YYYY-MM-DD'));
    }
    setCachedDocument(updatedCachedDocument);
  };

  const isDisabledTime = (hour: number): boolean => {
    const minDate = new Date();
    if (plannedDate.getFullYear() === minDate.getFullYear() &&
      plannedDate.getMonth() === minDate.getMonth() &&
      plannedDate.getDate() === minDate.getDate()) {
      const minTime = minDate.getHours() + 1;
      if (hour > minTime) {
        return false;
      }
      return true;
    }
    return false;
  }

  const CONTENT = 'content';
  const META = 'meta';
  const SOCIAL = 'social';
  const MAX_LENGTH_FB_LI_POST = 2500;
  const MAX_LENGTH_TW_POST = 140;
  const [tabKey, setTabKey] = useState(CONTENT);

  const editorRef = useRef<ReRenderAction>(null);

  const onExecutePromptFlow = async (promptFlow: PromptFlow) => {
    try {
      const promptExecution = await onGenerateContent(promptFlow.id!);
      sendLog(`Prompt execution: ${JSON.stringify(promptExecution)}`, LogLevel.info, promptExecution);
      let results = promptExecution.parsedResult;
      const updatedDocument: Document = { ...docRef.current! };
      switch (promptFlow.targetAction) {
        case TargetAction.GenerateFacebookPost:
          if (results.length > MAX_LENGTH_FB_LI_POST) {
            results = results.substring(0, MAX_LENGTH_FB_LI_POST);
          }
          updatedDocument.fbMessageTmp = results;
          setCachedDocument(updatedDocument);
          setTabKey(SOCIAL);
          document.getElementsByName('fbMessageTmp')[0].focus();
          break;
        case TargetAction.GenerateLinkedInPost:
          if (results.length > MAX_LENGTH_FB_LI_POST) {
            results = results.substring(0, MAX_LENGTH_FB_LI_POST);
          }
          updatedDocument.liMessageTmp = results;
          setCachedDocument(updatedDocument);
          setTabKey(SOCIAL);
          document.getElementsByName('liMessageTmp')[0].focus();
          break;
        case TargetAction.GenerateTwitterPost:
          if (results.length > MAX_LENGTH_TW_POST) {
            results = results.substring(0, MAX_LENGTH_TW_POST);
          }
          updatedDocument.twMessageTmp = results;
          setCachedDocument(updatedDocument);
          setTabKey(SOCIAL);
          document.getElementsByName('twMessageTmp')[0].focus();
          break;
        case TargetAction.GenerateArticleTitle:
          updatedDocument.title = results;
          setCachedDocument(updatedDocument);
          document.getElementsByName('title')[0].focus();
          break;
        case TargetAction.GenerateArticleOutline:
        case TargetAction.GenerateArticleContent:
        default:
          updatedDocument.content = results;
          setCachedDocument(updatedDocument);
          editorRef.current!.reRender();
          break;
      }
    } catch (err: any) {
      notify('Request to Chat GPT service timed out, please retry your last action', NotificationType.Error);
      sendLog(`Promptflow id: ${promptFlow.id} failed to execute with error: ${JSON.stringify(err)}`, LogLevel.error, { promptFlow, err });
    }
  };

  if ((id !== newEntryId && !loadedDocument.id) || statuses.length === 0 || !selectedProjectId) {
    return <div className="EditDocument">Loading document...</div>;
  }
  const prepublishedStatus: DocumentStatus = statuses.find((s) => s.type === 'PREPUBLISHED')!;
  const publishedStatus: DocumentStatus = statuses.find((s) => s.type === 'PUBLISHED')!;
  const errorStatus: DocumentStatus = statuses.find((s) => s.type === 'ERROR')!;
  const unselectableStatuses: DocumentStatus[] = [prepublishedStatus, publishedStatus, errorStatus];

  const uneditableStatuses: DocumentStatus[] = [prepublishedStatus, publishedStatus];
  const isUneditableStatus = uneditableStatuses.some((s) => s.id === cachedDocument.statusId);

  return (
    <div className="document-editor">
      {isGeneratingContent && (
        <div className="loading">
          <Spinner animation="border" variant="warning" />
        </div>
      )}

      <Tabs
        defaultActiveKey={CONTENT}
        activeKey={tabKey}
        onSelect={(k: any) => setTabKey(k)}
        transition={false}
        id="document-editor-tabs"
        className="mb-3 pt-2 doc-tabs"
      >
        <Tab key={1} eventKey={CONTENT} title="Content">
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label className="visually-hidden">Article title</Form.Label>
              <Form.Control
                type="text"
                value={cachedDocument.title}
                onChange={onDocumentFieldChange}
                aria-label="title"
                name="title"
                placeholder="Headline"
                className="mb-2 headline-textbox"
                tabIndex={1}
              />
              <Form.Control
                type="text"
                value={cachedDocument.shortDescription}
                onChange={onDocumentFieldChange}
                aria-label="Short Description"
                name="shortDescription"
                placeholder="Enter short description here..."
                className="no-border mb-2 font-italic short-description"
                tabIndex={2}
              />
            </Form.Group>
          </Row>
          <Row>
            <CoverImageContainer
              bannerImage={bannerImage}
              setBannerImage={setBannerImage}
              tabIndex={3}
              documentId={documentId}
            />
          </Row>
          <Row className="mt-1">
            <Form.Group className="mb-3" as={Col} md={9} sm={12}>
              <Form.Label className="visually-hidden">Article body</Form.Label>
              <div className='rich-editor' >
                <BjxArticleEditor
                  predefinedLinks={predefinedLinks}
                  placeholderText="Start writing your article here..."
                  content={cachedDocument.content}
                  onEditorChange={onEditorChange}
                  tabIndex={4}
                  ref={editorRef}
                />
              </div>
            </Form.Group>
            <EditorSidebar comments={comments} onExecuteAction={onExecutePromptFlow} onAddComment={() => false} />
          </Row>
        </Tab>

        <Tab key={2} eventKey={META} title="Settings" tabIndex={5}>
          <Row>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Date</Form.Label>
              {isUneditableStatus ? (
                <Form.Control
                  type="date"
                  value={moment(cachedDocument.plannedDate).format('YYYY-MM-DD')}
                  readOnly
                  disabled />
              ) : (
                <InputGroup>
                  <Form.Control
                    type="date"
                    value={selectedDateString}
                    min={moment(new Date()).format('YYYY-MM-DD')}
                    max={moment(new Date()).add(1, 'years').format('YYYY-MM-DD')}
                    onChange={onPlannedDateChange}
                    onBlur={onSelectPlannedDate}
                    aria-label="planned date"
                    name="plannedDate.date"
                    tabIndex={6}
                    required
                  />
                  <InputGroupText>
                    <Form.Select
                      value={plannedTime}
                      onChange={onSelectPlannedTime}
                      aria-label='hour to publish within'
                      name="plannedDate.time"
                      tabIndex={7}
                      required
                    >
                      <option value="1" disabled={isDisabledTime(1)}>1:00 AM</option>
                      <option value="2" disabled={isDisabledTime(2)}>2:00 AM</option>
                      <option value="3" disabled={isDisabledTime(3)}>3:00 AM</option>
                      <option value="4" disabled={isDisabledTime(4)}>4:00 AM</option>
                      <option value="5" disabled={isDisabledTime(5)}>5:00 AM</option>
                      <option value="6" disabled={isDisabledTime(6)}>6:00 AM</option>
                      <option value="7" disabled={isDisabledTime(7)}>7:00 AM</option>
                      <option value="8" disabled={isDisabledTime(8)}>8:00 AM</option>
                      <option value="9" disabled={isDisabledTime(9)}>9:00 AM</option>
                      <option value="10" disabled={isDisabledTime(10)}>10:00 AM</option>
                      <option value="11" disabled={isDisabledTime(11)}>11:00 AM</option>
                      <option value="12" disabled={isDisabledTime(12)}>12:00 PM</option>
                      <option value="13" disabled={isDisabledTime(13)}>1:00 PM</option>
                      <option value="14" disabled={isDisabledTime(14)}>2:00 PM</option>
                      <option value="15" disabled={isDisabledTime(15)}>3:00 PM</option>
                      <option value="16" disabled={isDisabledTime(16)}>4:00 PM</option>
                      <option value="17" disabled={isDisabledTime(17)}>5:00 PM</option>
                      <option value="18" disabled={isDisabledTime(18)}>6:00 PM</option>
                      <option value="19" disabled={isDisabledTime(19)}>7:00 PM</option>
                      <option value="20" disabled={isDisabledTime(20)}>8:00 PM</option>
                      <option value="21" disabled={isDisabledTime(21)}>9:00 PM</option>
                      <option value="22" disabled={isDisabledTime(22)}>10:00 PM</option>
                      <option value="23" disabled={isDisabledTime(23)}>11:00 PM</option>
                      <option value="0" disabled={isDisabledTime(0)}>12:00 AM</option>
                    </Form.Select>
                  </InputGroupText>
                </InputGroup>
              )}
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Status</Form.Label>
              {isUneditableStatus ? (
                <Form.Control
                  type="text"
                  name="statusId"
                  tabIndex={8}
                  defaultValue={statuses.find((s) => s.id === cachedDocument.statusId)?.name}
                  disabled
                  readOnly
                />
              ) : (
                <>
                  <Form.Select
                    aria-label="status"
                    onChange={onSelectionChange}
                    value={cachedDocument.statusId || statuses[0].id}
                    name="statusId"
                    tabIndex={9}
                  >
                    {statuses.map((s) => (
                      <option
                        value={s.id}
                        key={s.id}
                        disabled={unselectableStatuses.some((us) => us.id === s.id)}
                      >
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                  <WorkflowHelp />
                </>
              )}
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Category</Form.Label>
              {(sections.length > 0) ?
                <Form.Select
                  aria-label="category"
                  onChange={onSelectionChange}
                  value={cachedDocument.siteSectionId || sections[0].id}
                  name="siteSectionId"
                  tabIndex={10}
                >
                  {sections.map((s) => (
                    <option value={s.id} key={s.id}>
                      {s.sectionName}
                    </option>
                  ))}
                </Form.Select>
                :
                <>
                  <Form.Control
                    readOnly
                    value="Uncategorized"
                    aria-label="category-error"
                    tabIndex={11}
                  />
                  <a
                    className='form-text text-muted'
                    href="https://blabberjax.atlassian.net/wiki/spaces/BKP/pages/426770433/No+Categories+Available+in+Editor+Dropdown"
                    target="_blank"
                    rel="noreferrer">
                    Expecting categories? Get help here.
                  </a>
                </>
              }
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Theme</Form.Label>
              <AsyncSelect
                loadOptions={searchThemes}
                value={selectedTheme}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.body}
                menuPosition='fixed'
                className="basic-multi-select"
                classNamePrefix="select"
                name="themeId"
                onChange={(selection: SingleValue<{ value: string, label: string }>) => handleThemeChanged(selection)}
                closeMenuOnSelect
                isClearable
                tabIndex={12}
                data-lpignore="true"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={cachedDocument.articleType}
                onChange={onSelectionChange}
                aria-label="article type"
                name="articleType"
                id="articleType"
                tabIndex={13}
              >
                {Object.keys(ArticleType).map((s) => (
                  <option value={s} key={s}>
                    {s.replace('_', ' ')}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Target Length</Form.Label>
              <Form.Control
                type="number"
                value={cachedDocument.targetLength}
                onChange={onDocumentFieldChange}
                aria-label="target length"
                name="targetLength"
                id="targetLength"
                tabIndex={14}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md={6}>
              <Form.Label>Target Keywords</Form.Label>
              <Form.Control
                type="text"
                value={cachedKeyword.seoKeyword}
                onChange={onKeywordFieldChange}
                aria-label="SEO Keyword"
                name="seoKeyword"
                id="seoKeyword"
                placeholder="Keywords, comma separated"
                tabIndex={15}
              />
            </Form.Group>
            <Form.Group as={Col} md={3}>
              <Form.Label>Search Volume</Form.Label>
              <Form.Control
                type="number"
                value={cachedKeyword.seoMonthlySearchVolume}
                onChange={onKeywordFieldChange}
                aria-label="Search Volume"
                name="seoMonthlySearchVolume"
                tabIndex={16}
              />
            </Form.Group>
            <Form.Group as={Col} md={3}>
              <Form.Label>Difficulty</Form.Label>
              <Form.Control
                type="number"
                value={cachedKeyword.seoDifficulty}
                onChange={onKeywordFieldChange}
                aria-label="Difficulty"
                name="seoDifficulty"
                tabIndex={17}
              />
            </Form.Group>
          </Row>
        </Tab>

        <Tab key={3} eventKey={SOCIAL} title="Social Posts" tabIndex={16}>
          <Card className="mb-3 col-md-12">
            <Form.Group>
              <Form.Label className="bg-blue">
                <i className="bi bi-linkedin" />
                LinkedIn Message
              </Form.Label>
              <Form.Control
                type="text"
                value={cachedDocument.liMessageTmp}
                onChange={onDocumentFieldChange}
                as="textarea"
                rows={5}
                aria-label="LinkedIn Message"
                name="liMessageTmp"
                placeholder="Enter your LinkedIn message here..."
                className="no-border"
                tabIndex={18}
                maxLength={MAX_LENGTH_FB_LI_POST}
              />
            </Form.Group>
          </Card>
          <Card className="mb-3 col-md-12">
            <Form.Group>
              <Form.Label className="bg-blue">
                <i className="bi bi-facebook" />
                Facebook Message
              </Form.Label>
              <Form.Control
                type="text"
                value={cachedDocument.fbMessageTmp}
                onChange={onDocumentFieldChange}
                as="textarea"
                rows={5}
                aria-label="Facebook Message"
                name="fbMessageTmp"
                placeholder="Enter your Facebook message here..."
                className="no-border"
                tabIndex={19}
                maxLength={MAX_LENGTH_FB_LI_POST}
              />
            </Form.Group>
          </Card>
          <Card className="mb-3 col-md-12">
            <Form.Group>
              <Form.Label className="bg-blue">
                <i className="bi bi-twitter-x" />X Message
              </Form.Label>
              <Form.Control
                type="text"
                value={cachedDocument.twMessageTmp}
                onChange={onDocumentFieldChange}
                as="textarea"
                rows={3}
                maxLength={MAX_LENGTH_TW_POST}
                aria-label="X Message"
                name="twMessageTmp"
                placeholder="Enter your X message here..."
                className="no-border"
                tabIndex={20}
              />
            </Form.Group>
          </Card>
        </Tab>
      </Tabs>

      <Row className="fixed-bottom approval-bar">
        <Col md={12} className="btn-sticky-top pull-right">
          <div className="action-buttons">
            <button
              type="button"
              id="btnSendForReview"
              onClick={() => { onSave(false, DocumentStatusKey.PENDING_APPROVAL) }}
              className="btn btn-primary mb-2 mx-2"
              title="Request immediate review and approval"
              tabIndex={21}
            >
              <i className="bi bi-person-check-fill px-2 xxs-hide" />
              Send for Review
            </button>
            <button
              type="button"
              onClick={() => { onSave(false) }}
              className="btn btn-success mb-2 mx-2"
              tabIndex={22}
              title='Save or schedule this article for a later date'
            >
              <i className="bi bi-calendar-date px-2 xxs-hide" />
              Schedule
            </button>
            <button
              type="button"
              onClick={() => { onSave(true) }}
              className="btn btn-light mb-2 mx-2"
              title="Publish immediately to your CMS and Social Media Channels"
              tabIndex={23}
            >
              <i className="bi bi-cloud-upload px-2 xxs-hide" />
              Publish
            </button>
            <button
              type="button"
              onClick={onSaveAndNew}
              className="btn btn-secondary mb-2"
              tabIndex={24}
              title='Save and then create a new article'
            >
              <i className="bi bi-calendar-plus px-2 xxs-hide" />
              Schedule &amp; New
            </button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EditDocument;
